import React, { useEffect, useRef, useState } from 'react';
import { FaGoogle, FaSync } from 'react-icons/fa';
import './GoogleAnalyticsEmbed.css';

const GoogleAnalyticsEmbed = () => {
  const authButtonRef = useRef(null);
  const timelineRef = useRef(null);
  const sessionsRef = useRef(null);
  const usersRef = useRef(null);
  const bounceRateRef = useRef(null);
  const pageviewsRef = useRef(null);
  const deviceCategoryRef = useRef(null);
  const trafficSourceRef = useRef(null);
  
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Load the Google Analytics Embed API
    const script = document.createElement('script');
    script.src = 'https://www.gstatic.com/charts/loader.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.google.charts.load('current', { packages: ['corechart', 'table'] });
      window.google.charts.setOnLoadCallback(initEmbedApi);
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Clean up any global variables or event listeners
      if (window.gapi && window.gapi.analytics) {
        // Perform any necessary cleanup
      }
    };
  }, []);

  const initEmbedApi = () => {
    // Load the Embed API JavaScript
    const embedScript = document.createElement('script');
    embedScript.src = 'https://ga-dev-tools.appspot.com/embed-api/embed-api.js';
    embedScript.async = true;
    document.body.appendChild(embedScript);

    embedScript.onload = () => {
      // Initialize the Embed API
      window.gapi.analytics.ready(() => {
        // Set the client ID from environment variable
        const clientId = process.env.REACT_APP_GOOGLE_ANALYTICS_CLIENT_ID;
        
        // Configure the auth
        window.gapi.analytics.auth.authorize({
          container: authButtonRef.current,
          clientid: clientId || '1234567890-abcdefghijklmnopqrstuvwxyz.apps.googleusercontent.com', // Fallback
        });

        // Add event listeners
        window.gapi.analytics.auth.on('success', () => {
          setIsAuthorized(true);
          setIsLoading(true);
          renderCharts();
        });

        window.gapi.analytics.auth.on('error', (err) => {
          setError(`Authentication error: ${err.message || 'Unknown error'}`);
          setIsLoading(false);
        });
      });
    };
  };

  const renderCharts = () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Define dark mode chart options
      const darkModeOptions = {
        backgroundColor: 'transparent',
        chartArea: { width: '90%', height: '80%' },
        legend: { 
          textStyle: { color: '#e2e8f0' },
          position: 'top'
        },
        titleTextStyle: { color: '#e2e8f0' },
        hAxis: {
          textStyle: { color: '#94a3b8' },
          gridlines: { color: 'rgba(255, 255, 255, 0.05)' }
        },
        vAxis: {
          textStyle: { color: '#94a3b8' },
          gridlines: { color: 'rgba(255, 255, 255, 0.05)' }
        },
        colors: ['#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'],
        tooltip: { textStyle: { color: '#0f172a' } }
      };

      // Timeline Chart
      const timelineChart = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'dimensions': 'ga:date',
          'metrics': 'ga:sessions,ga:users',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'LINE',
          container: timelineRef.current,
          options: {
            ...darkModeOptions,
            title: 'Sessions & Users (Last 30 Days)'
          }
        }
      });

      // Sessions Metric
      const sessionsMetric = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'metrics': 'ga:sessions',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'TABLE',
          container: sessionsRef.current,
          options: {
            ...darkModeOptions,
            title: 'Total Sessions'
          }
        }
      });

      // Users Metric
      const usersMetric = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'metrics': 'ga:users',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'TABLE',
          container: usersRef.current,
          options: {
            ...darkModeOptions,
            title: 'Total Users'
          }
        }
      });

      // Pageviews Metric
      const pageviewsMetric = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'metrics': 'ga:pageviews',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'TABLE',
          container: pageviewsRef.current,
          options: {
            ...darkModeOptions,
            title: 'Total Pageviews'
          }
        }
      });

      // Bounce Rate Metric
      const bounceRateMetric = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'metrics': 'ga:bounceRate',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'TABLE',
          container: bounceRateRef.current,
          options: {
            ...darkModeOptions,
            title: 'Bounce Rate'
          }
        }
      });

      // Device Category Chart
      const deviceCategoryChart = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'dimensions': 'ga:deviceCategory',
          'metrics': 'ga:sessions',
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'PIE',
          container: deviceCategoryRef.current,
          options: {
            ...darkModeOptions,
            title: 'Sessions by Device Category',
            pieHole: 0.4
          }
        }
      });

      // Traffic Source Chart
      const trafficSourceChart = new window.gapi.analytics.googleCharts.DataChart({
        reportType: 'ga',
        query: {
          'ids': 'ga:' + process.env.REACT_APP_GOOGLE_ANALYTICS_STREAM_ID,
          'dimensions': 'ga:source',
          'metrics': 'ga:sessions',
          'sort': '-ga:sessions',
          'max-results': 5,
          'start-date': '30daysAgo',
          'end-date': 'today'
        },
        chart: {
          type: 'PIE',
          container: trafficSourceRef.current,
          options: {
            ...darkModeOptions,
            title: 'Top 5 Traffic Sources',
            pieHole: 0.4
          }
        }
      });

      // Execute all charts
      Promise.all([
        timelineChart.execute(),
        sessionsMetric.execute(),
        usersMetric.execute(),
        pageviewsMetric.execute(),
        bounceRateMetric.execute(),
        deviceCategoryChart.execute(),
        trafficSourceChart.execute()
      ]).then(() => {
        setIsLoading(false);
      }).catch(err => {
        console.error('Error rendering charts:', err);
        setError('Failed to load Google Analytics data. Please try again.');
        setIsLoading(false);
      });
    } catch (err) {
      console.error('Error setting up charts:', err);
      setError('Failed to set up Google Analytics charts. Please try again.');
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (isAuthorized && !refreshing) {
      setRefreshing(true);
      renderCharts();
      setTimeout(() => {
        setRefreshing(false);
      }, 1000);
    }
  };

  return (
    <div className="analytics-section">
      <div className="section-header">
        <div className="section-title">
          <FaGoogle />
          <h2>Google Analytics</h2>
        </div>
        {isAuthorized && (
          <button 
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
          >
            <FaSync className={refreshing ? 'spinning' : ''} />
            Refresh
          </button>
        )}
      </div>
      
      {!isAuthorized && (
        <div className="auth-container">
          <p>Please authorize with Google Analytics to view your data:</p>
          <div ref={authButtonRef}></div>
        </div>
      )}
      
      {isAuthorized && isLoading && !refreshing ? (
        <div className="section-loading">
          <div className="loading-spinner"></div>
          <p>Loading Google Analytics data...</p>
        </div>
      ) : isAuthorized && error ? (
        <div className="section-error">
          <p>{error}</p>
          <button onClick={renderCharts} className="retry-button">
            Try Again
          </button>
        </div>
      ) : isAuthorized && (
        <div className="analytics-embed-container">
          <div className="chart-container timeline-chart">
            <div ref={timelineRef}></div>
          </div>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div ref={sessionsRef}></div>
            </div>
            
            <div className="metric-card">
              <div ref={usersRef}></div>
            </div>
            
            <div className="metric-card">
              <div ref={pageviewsRef}></div>
            </div>
            
            <div className="metric-card">
              <div ref={bounceRateRef}></div>
            </div>
          </div>
          
          <div className="charts-grid">
            <div className="chart-card">
              <div ref={deviceCategoryRef}></div>
            </div>
            
            <div className="chart-card">
              <div ref={trafficSourceRef}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleAnalyticsEmbed; 